import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo, useRef, useState } from "react";
import Markdown from "react-native-markdown-display";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ButtonRef, RadioGroupRef } from "@ptsd/uikit";
import { Button, Key, RadioGroup, stylex, useKeyTrigger } from "@ptsd/uikit";
import { ArrowLeft } from "@tamagui/lucide-icons";
import type { UseFormReturn } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import type { XStackProps } from "tamagui";
import { Text, useMedia, View, XStack } from "tamagui";
import { z } from "zod";

import { KeyboardIcon } from "~/component/icons";

interface FormItem {
  id: string;
  options: {
    code: string | null;
    id: string;
    type: "integer" | "coding" | null;
    text: string;
    emoji: string | null;
  }[];
  question: string;
  questionnaireId: string | undefined;
  questionnaireName: string | undefined;
  questionnaireTitle: string | null | undefined;
}

//TODO: i18n

const funnelFormData = z.record(z.array(z.string()));
type FunnelFormData = z.infer<typeof funnelFormData>;

interface FunnelContext {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  items: FormItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<Record<string, string[]>, any, undefined> | undefined;
  onSubmit: (values: FunnelFormData) => void;
}

const FunnelContext = createContext<FunnelContext>({
  step: 0,
  setStep: () => console.warn("should be used inside funnel context provider"),
  items: [],
  form: undefined,
  onSubmit: () => console.warn("should be used inside funnel context provider"),
});

const getDefaultFromValues = (items: FormItem[]) => {
  return items.reduce<Record<string, string[]>>((acc, cur) => {
    acc[cur.id] = [];
    return acc;
  }, {});
};

const FunnelProvider: React.FC<
  PropsWithChildren & {
    items: FormItem[];
    onSubmit: (values: FunnelFormData) => void;
  }
> = ({ children, items, onSubmit }) => {
  const [step, setStep] = useState(0);

  const form = useForm<FunnelFormData>({
    resolver: zodResolver(funnelFormData),
    values: getDefaultFromValues(items),
  });

  return (
    <FunnelContext.Provider
      value={{
        step,
        setStep,
        items,
        form,
        onSubmit,
      }}
    >
      {children}
    </FunnelContext.Provider>
  );
};

export const useFunnel = () => {
  const context = useContext(FunnelContext);
  if (!context) {
    throw new Error("should be used inside funnel context provider");
  }

  const itemsLength = context.items.length;

  const isLastQuestion = context.step === itemsLength - 1;

  const inputName = context.items[context.step]?.id ?? "";
  const currentInputValue = context.form?.watch()[inputName];

  const {
    id: questionnaireId,
    options,
    question,
    questionnaireTitle,
    questionnaireName,
  } = context.items[context.step] ?? {};

  const keyMap = useMemo(() => {
    const options = context.items[context.step]?.options;
    if (options) {
      const keys = Object.fromEntries(
        Object.entries(options).map(([_, value], index) => {
          return [(index + 1).toString(), value.id];
        }),
      );
      return keys;
    }
  }, [context]);

  const next = () => {
    if (isLastQuestion) {
      context.setStep(0);
      const values = context.form?.getValues();
      if (values) {
        context.onSubmit(values);
      }
      return;
    }

    context.setStep((prev) => {
      if (prev === itemsLength - 1) return prev;
      return prev + 1;
    });
  };

  const prev = () => {
    context.setStep((prev) => {
      if (prev === 0) return prev;
      return prev - 1;
    });
  };

  return {
    question: {
      text: question,
      questionnaireTitle,
    },
    options,
    keyMap,
    isLastQuestion,
    isFirsQuestion: context.step === 0,
    inputName,
    isNextButtonDisabled: !currentInputValue || currentInputValue.length === 0,
    next,
    prev,
    questionnaireName,
    questionnaireId,
    ...context,
  };
};

const FunnelContainer: React.FC<XStackProps> = ({ children, ...rest }) => {
  return (
    <View
      data-id={"funnel-container"}
      flexDirection="row"
      paddingTop={80}
      justifyContent="center"
      $gtSm={{
        space: "$11",
      }}
      flex={2}
      paddingHorizontal={24}
      $sm={{
        flexDirection: "column",
        paddingTop: "$5.1",
        justifyContent: "space-between",
        space: "$5.1",
        alignItems: "center",
      }}
      {...rest}
    >
      {children}
    </View>
  );
};

const FunnelQuestion = () => {
  const { question, step, questionnaireName } = useFunnel();
  const { sm } = useMedia();

  return (
    <View
      $sm={{
        width: "100%",
        gap: "$1",
      }}
      flex={1}
      width={"50%"}
      maxHeight={658}
      maxWidth={556}
      gap={"$2"}
      data-id={"funnel-question"}
      data-questionnaire_id={questionnaireName}
      data-index={step}
    >
      {question.questionnaireTitle && (
        <View data-id={"funnel-questionnaire-title"} data-index={step}>
          <Markdown
            style={{
              body: {
                fontFamily: "Fixel Text",
                fontWeight: 400,
                fontSize: sm ? 16 : 24,
                lineHeight: sm ? 24 : 30,
                color: "#23252A",
              },
              em: {
                fontFamily: "Fixel Text",
                fontWeight: 700,
                fontSize: sm ? 16 : 24,
                lineHeight: sm ? 24 : 30,
                color: "#23252A",
              },
            }}
          >
            {question.questionnaireTitle}
          </Markdown>
        </View>
      )}

      <Text
        data-id={"funnel-question"}
        data-index={step}
        fontFamily={"$subtitle"}
        fontWeight={"$6"}
        color={"$gray-primary-900"}
        {...stylex([
          sm,
          {
            fontSize: "$4",
            lineHeight: "$4",
          },
        ])}
        {...stylex([
          !sm,
          {
            fontSize: "$7",
            lineHeight: "$7",
          },
        ])}
      >
        {question.text}
      </Text>
    </View>
  );
};

const CONTROLS_KEY_BINDINGS = {
  Enter: "Enter",
  Escape: "Escape",
};

const FunnelRadiogroup = ({ isLoading }: { isLoading?: boolean }) => {
  const {
    isNextButtonDisabled,
    isFirsQuestion,
    form,
    keyMap,
    inputName,
    options,
    next,
    prev,
  } = useFunnel();

  const { sm, lg } = useMedia();
  const itemsRef = useRef<(RadioGroupRef | null)[]>([]);
  const controlsRef = useRef<(ButtonRef | null)[]>([]);

  useKeyTrigger(CONTROLS_KEY_BINDINGS, (value) => {
    controlsRef.current?.find((item) => item?.ids?.includes(value))?.press();
  });

  useKeyTrigger(
    keyMap,
    (value) => itemsRef.current.find((item) => item?.value === value)?.click(),
    options && options?.length > 6,
  );

  if (!inputName || !options?.length) return null;

  return (
    <View
      $sm={{
        width: "100%",
        gap: "$6",
        paddingBottom: "$3",
        justifyContent: "flex-end",
      }}
      flex={1}
      width={"50%"}
      maxWidth={556}
      maxHeight={658}
      justifyContent="space-between"
      gap={"$2"}
    >
      <View gap={"$3"}>
        <Controller
          key={inputName}
          name={inputName}
          control={form?.control}
          render={({ field }) => (
            <RadioGroup.Container
              data-id={"radio-group"}
              withScroll={options.length > 5}
              maxHeight={440}
              key={inputName}
              value={field.value ? field.value[0] : undefined}
              onValueChange={(value) => {
                field.onChange([value]);
              }}
              $sm={{
                gap: "$1.5",
              }}
              gap={"$2"}
              sm={sm}
            >
              {options?.map(({ id, text, code }, index) => {
                return (
                  <RadioGroup.Item
                    data-option_id={id}
                    data-code={code}
                    data-id={"radio-item"}
                    rounded="lg"
                    size={sm ? "md" : "lg"}
                    ref={(node) => {
                      itemsRef.current[index] = node;
                    }}
                    key={id}
                    index={index}
                    value={id}
                    disabled={isLoading}
                    renderKey={(props) => {
                      if (sm) return null;
                      if (options.length > 6) return null;
                      return (
                        <RadioGroup.Key
                          size="sm"
                          text={!lg ? "Клавіша" : ""}
                          count={index + 1}
                          {...props}
                        />
                      );
                    }}
                    text={text}
                  />
                );
              })}
            </RadioGroup.Container>
          )}
        />
        {!sm && (
          <XStack gap={"$1"} alignItems="center" alignSelf="center">
            <KeyboardIcon />
            <Text
              fontFamily={"$body"}
              fontWeight={"$5"}
              fontSize={"$3"}
              lineHeight={"$3"}
              color={"$gray-800"}
            >
              Вибирайте відповідь, натиснувши цифру на клавіатурі
            </Text>
          </XStack>
        )}
      </View>

      {sm && (
        <XStack gap={"$1"} alignItems="center">
          <Button
            data-id={"back"}
            disabled={isFirsQuestion || isLoading}
            onPress={prev}
            mode="icon"
            variant="gray"
            size="md"
            rounded="lg"
            height={"$7"}
            width={"$7"}
          >
            <ArrowLeft />
          </Button>

          <Button
            data-id={"confirm"}
            flex={1}
            height={"$7"}
            disabled={isNextButtonDisabled || isLoading}
            width={"100%"}
            variant="primary"
            size={"md"}
            rounded="lg"
            onPress={next}
          >
            Підтвердити
          </Button>
        </XStack>
      )}
      {!sm && (
        <XStack
          maxHeight={112}
          flex={2}
          paddingBottom={"$1"}
          width={"100%"}
          gap={"$2"}
        >
          <View
            flex={1}
            width={"50%"}
            alignItems="center"
            justifyContent="space-between"
            gap={"$1.5"}
          >
            <Button
              data-id={"back"}
              disabled={isFirsQuestion || isLoading}
              ref={(node) => (controlsRef.current[0] = node)}
              onPress={prev}
              ids={["Escape"]}
              width={"100%"}
              variant="outlined"
              size="lg"
              rounded="lg"
            >
              Назад
            </Button>
            <XStack gap={"$1.5"} alignItems="center">
              <Text
                fontFamily={"$body"}
                fontWeight={"$4"}
                fontSize={"$3"}
                lineHeight={"$3"}
                color={"$gray-600"}
              >
                Клавіша
              </Text>

              <Key size="lg" text={"Esc"} iconProps={{ width: 48 }} />
            </XStack>
          </View>

          <View
            flex={1}
            width={"50%"}
            alignItems="center"
            justifyContent="space-between"
            gap={"$1.5"}
          >
            <Button
              data-id={"confirm"}
              disabled={isNextButtonDisabled || isLoading}
              ref={(node) => (controlsRef.current[1] = node)}
              width={"100%"}
              ids={["Enter"]}
              variant="primary"
              size={"lg"}
              rounded="lg"
              onPress={next}
            >
              Підтвердити
            </Button>

            <XStack gap={"$1.5"} alignItems="center">
              <Text
                fontFamily={"$body"}
                fontWeight={"$4"}
                fontSize={"$3"}
                lineHeight={"$3"}
                color={"$gray-600"}
              >
                Клавіша
              </Text>

              <Key size="lg" text={"Enter"} iconProps={{ width: 64 }} />
            </XStack>
          </View>
        </XStack>
      )}
    </View>
  );
};

export const Funnel = {
  Options: FunnelRadiogroup,
  Provider: FunnelProvider,
  Question: FunnelQuestion,
  Container: FunnelContainer,
};
